import { useState, useEffect } from "react";
import { HandHelping, CheckCircle, ChevronDown, MapPin, User, Mail, Phone, Sparkles, Wrench, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { SEO } from "../components/SEO";
import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Multi-select dropdown component
const MultiSelect = ({ label, icon: Icon, options, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);

  const toggle = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative">
      <Label className="text-[#1A1915] font-medium flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4 text-[#2C4A3B]" />
        {label}
      </Label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-white border-2 border-[#D1CFC5] rounded-lg px-4 py-2.5 text-left hover:border-[#2C4A3B] transition-colors"
        data-testid={testId}
      >
        <span className={selected.length ? "text-[#1A1915]" : "text-[#5C5A52]"}>
          {selected.length ? `${selected.length} selected` : `Select ${label.toLowerCase()}...`}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#5C5A52] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border-2 border-[#D1CFC5] rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {options.map((option) => (
            <label
              key={option}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#F7F6F2] cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggle(option)}
                className="w-4 h-4 accent-[#2C4A3B] rounded"
              />
              <span className="text-sm text-[#1A1915]">{option}</span>
            </label>
          ))}
        </div>
      )}

      {/* Selected tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-1 bg-[#2C4A3B]/10 text-[#2C4A3B] text-xs font-medium px-2.5 py-1 rounded-full"
            >
              {item}
              <button
                type="button"
                onClick={() => toggle(item)}
                className="hover:text-red-500 ml-0.5"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const Volunteer = () => {
  const [options, setOptions] = useState({
    experience: [], talents: [], efforts: [], availability: [], towns: []
  });
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    town: "", experience: "", talents: [], efforts: [], availability: [], bio: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [approvedVolunteers, setApprovedVolunteers] = useState([]);

  useEffect(() => {
    fetchOptions();
    fetchApprovedVolunteers();
  }, []);

  const fetchOptions = async () => {
    try {
      const res = await axios.get(`${API}/volunteers/options`);
      setOptions(res.data);
    } catch (e) {
      console.error("Error fetching options:", e);
    }
  };

  const fetchApprovedVolunteers = async () => {
    try {
      const res = await axios.get(`${API}/volunteers/approved`);
      setApprovedVolunteers(res.data);
    } catch (e) {
      console.error("Error fetching approved volunteers:", e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.town || !form.experience) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.talents.length === 0) {
      toast.error("Please select at least one talent");
      return;
    }
    if (form.efforts.length === 0) {
      toast.error("Please select at least one area of effort");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/volunteers`, form);
      setSubmitted(true);
      toast.success("Thank you for volunteering!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#EFEDE5]">
        <NavbarLight />
        <section className="py-20 px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#C0C0C0] to-[#D4A017] flex items-center justify-center mx-auto mb-6">
              <HandHelping className="w-12 h-12 text-white" />
            </div>
            <CheckCircle className="w-12 h-12 text-[#2C4A3B] mx-auto mb-4" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1915] mb-4" data-testid="volunteer-success-title">
              You're In!
            </h1>
            <p className="text-xl text-[#5C5A52] mb-3">
              Welcome to the EK Valleys Volunteer family.
            </p>
            <p className="text-[#5C5A52] mb-8">
              We'll be in touch soon with opportunities that match your skills and availability. Your Volunteer badge is on its way!
            </p>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C0C0C0]/20 to-[#D4A017]/20 border-2 border-[#C0C0C0] text-[#1A1915] font-semibold px-6 py-3 rounded-full mb-8" data-testid="volunteer-badge-preview">
              <HandHelping className="w-5 h-5 text-[#D4A017]" />
              EK Valleys Volunteer
            </div>
            <div className="flex justify-center gap-4">
              <Button
                onClick={() => window.location.href = "/directory"}
                className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold px-8"
              >
                Browse Directory
              </Button>
              <Button
                onClick={() => window.location.href = "/"}
                variant="outline"
                className="font-semibold px-8"
              >
                Back Home
              </Button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO
        title="Volunteer | ekvalleys.com"
        description="Join the EK Valleys volunteer team and help build community in the East Kootenay. Share your talents, experience, and time."
        keywords="volunteer East Kootenay, community, EK Valleys volunteer, Invermere volunteer"
      />
      <NavbarLight />

      {/* Hero Banner */}
      <section className="bg-[#1A2F23] py-16 md:py-20 px-6 md:px-12 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C0C0C0] to-[#D4A017] flex items-center justify-center" data-testid="volunteer-hero-badge">
              <HandHelping className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="volunteer-page-title">
            Lend a Hand
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-xl mx-auto">
            The East Kootenay is built by people who show up. Tell us what you're good at — we'll match you with where you're needed.
          </p>
        </div>
      </section>

      {/* Volunteer Showcase */}
      {approvedVolunteers.length > 0 && (
        <section className="py-12 px-6 md:px-12 bg-[#F7F6F2] border-b-2 border-[#D1CFC5]" data-testid="volunteer-showcase">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-2 text-center">Our Volunteer Team</h2>
            <p className="text-[#5C5A52] text-center mb-8">Meet the people making East Kootenay stronger.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {approvedVolunteers.map(vol => (
                <div key={vol.id} className="bg-white border-2 border-[#D1CFC5] rounded-xl p-4 text-center" data-testid={`volunteer-card-${vol.id}`}>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C0C0C0] to-[#D4A017] flex items-center justify-center mx-auto mb-3">
                    <HandHelping className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-[#1A1915] text-sm">{vol.first_name} {vol.last_name?.[0]}.</p>
                  <p className="text-xs text-[#5C5A52] flex items-center justify-center gap-1 mt-1"><MapPin className="w-3 h-3" />{vol.town}</p>
                  {vol.talents && vol.talents.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-1 mt-2">
                      {vol.talents.slice(0, 2).map((t, i) => (
                        <span key={`${t}-${i}`} className="text-[9px] bg-[#2C4A3B]/10 text-[#2C4A3B] px-1.5 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  )}
                  <span className={`inline-block mt-2 text-[9px] font-bold px-2 py-0.5 rounded-full ${
                    vol.badge_level === "gold" ? "bg-[#D4A017]/20 text-[#D4A017]" : "bg-[#C0C0C0]/20 text-[#8A8A8A]"
                  }`}>{vol.badge_level === "gold" ? "Gold" : "Silver"} Volunteer</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Registration Form */}
      <section className="py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8">

            {/* Personal Info */}
            <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-6 md:p-8">
              <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-[#2C4A3B]" />
                About You
              </h2>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-[#1A1915] font-medium">First Name *</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="mt-1.5 border-[#D1CFC5] bg-[#F7F6F2]"
                    placeholder="Your first name"
                    data-testid="vol-first-name"
                  />
                </div>
                <div>
                  <Label className="text-[#1A1915] font-medium">Last Name *</Label>
                  <Input
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="mt-1.5 border-[#D1CFC5] bg-[#F7F6F2]"
                    placeholder="Your last name"
                    data-testid="vol-last-name"
                  />
                </div>
                <div>
                  <Label className="text-[#1A1915] font-medium flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-[#2C4A3B]" /> Email *
                  </Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 border-[#D1CFC5] bg-[#F7F6F2]"
                    placeholder="you@email.com"
                    data-testid="vol-email"
                  />
                </div>
                <div>
                  <Label className="text-[#1A1915] font-medium flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-[#2C4A3B]" /> Phone
                  </Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1.5 border-[#D1CFC5] bg-[#F7F6F2]"
                    placeholder="(250) 555-1234"
                    data-testid="vol-phone"
                  />
                </div>
                <div>
                  <Label className="text-[#1A1915] font-medium flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-[#2C4A3B]" /> Town *
                  </Label>
                  <Select value={form.town} onValueChange={(v) => setForm({ ...form, town: v })}>
                    <SelectTrigger className="mt-1.5 border-[#D1CFC5] bg-[#F7F6F2]" data-testid="vol-town-select">
                      <SelectValue placeholder="Select your town" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.towns.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#1A1915] font-medium flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#2C4A3B]" /> Experience *
                  </Label>
                  <Select value={form.experience} onValueChange={(v) => setForm({ ...form, experience: v })}>
                    <SelectTrigger className="mt-1.5 border-[#D1CFC5] bg-[#F7F6F2]" data-testid="vol-experience-select">
                      <SelectValue placeholder="Your volunteer experience" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.experience.map((e) => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Skills & Efforts */}
            <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-6 md:p-8">
              <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-6 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-[#2C4A3B]" />
                Your Skills & How You Want to Help
              </h2>
              <div className="space-y-6">
                <MultiSelect
                  label="Talents & Skills"
                  icon={Sparkles}
                  options={options.talents}
                  selected={form.talents}
                  onChange={(v) => setForm({ ...form, talents: v })}
                  testId="vol-talents-dropdown"
                />
                <MultiSelect
                  label="Areas of Effort"
                  icon={HandHelping}
                  options={options.efforts}
                  selected={form.efforts}
                  onChange={(v) => setForm({ ...form, efforts: v })}
                  testId="vol-efforts-dropdown"
                />
                <MultiSelect
                  label="Availability"
                  icon={Clock}
                  options={options.availability}
                  selected={form.availability}
                  onChange={(v) => setForm({ ...form, availability: v })}
                  testId="vol-availability-dropdown"
                />
              </div>
            </div>

            {/* Bio */}
            <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-6 md:p-8">
              <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-4">
                Anything Else?
              </h2>
              <Textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="border-[#D1CFC5] bg-[#F7F6F2]"
                placeholder="Tell us a bit about yourself, why you want to volunteer, or anything else we should know..."
                rows={4}
                data-testid="vol-bio"
              />
            </div>

            {/* Badge Preview & Submit */}
            <div className="bg-gradient-to-br from-[#C0C0C0]/10 to-[#D4A017]/10 border-2 border-[#C0C0C0]/40 rounded-xl p-6 md:p-8 text-center">
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C0C0C0] to-[#D4A017] flex items-center justify-center">
                  <HandHelping className="w-7 h-7 text-white" />
                </div>
              </div>
              <p className="text-[#1A1915] font-medium mb-1">Your Volunteer Badge</p>
              <p className="text-[#5C5A52] text-sm mb-6">
                Every volunteer receives a silver & gold badge displayed alongside their contributions to the community.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold px-12 py-3 text-lg"
                data-testid="vol-submit-btn"
              >
                {submitting ? "Registering..." : "Register as Volunteer"}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Volunteer;
